/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import IconPlus from 'gfx/icons/plus-current.svg';
import IconMinus from 'gfx/icons/minus-current.svg';

import styles from './RoundButton.module.scss';

/**
 * This is the RoundButton component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function RoundButton({
  label,
  icon,
  className,
  onClick,
  disabled,
  showLabel,
  iconOnMobile,
  children,
  variant,
  ...props
}) {
  const ariaLabel = !showLabel || iconOnMobile ? label : null;

  const renderIcon = () => {
    switch (icon) {
      case 'plus':
        return <IconPlus />;

      case 'minus':
        return <IconMinus />;

      default:
        return `[${icon}]`;
    }
  };

  return (
    <button
      className={classNames(styles.main, className, styles[variant], {
        [styles.hideLabelOnMobile]: iconOnMobile && icon,
      })}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {icon && <span className={styles.icon}>{renderIcon()}</span>}
      <span className={styles.label}>
        {label}
        {children}
      </span>
    </button>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
RoundButton.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  label: PropTypes.string,
  icon: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  showLabel: PropTypes.bool, // If set to false, label will be added as aria-label
  iconOnMobile: PropTypes.bool, // If set to true, will only show the icon on small devices (and label will be added as aria-label)
  variant: PropTypes.oneOf(['default', 'outline']),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
RoundButton.defaultProps = {
  label: null,
  icon: null,
  className: null,
  onClick: () => {},
  disabled: false,
  showLabel: true,
  iconOnMobile: true,
  variant: 'default',
};

export default RoundButton;
